import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Award, BarChart3, BadgeCheck, Building2, CalendarCheck, CheckCircle2, ChevronDown, ClipboardList,
  CreditCard, FileSpreadsheet, Globe, GraduationCap, KeyRound, Landmark, Lock, Mail, MonitorSmartphone, Printer,
  QrCode, Receipt, ScanFace, ScanLine, ShieldCheck, Sparkles, Stethoscope, Ticket, Users, WifiOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PRIMARY_BTN, SiteFooter, SiteHeader } from '../components/home/SiteChrome';
import { CHECKIN_METHODS } from '../utils/checkInMethods';
import { useAuth } from '../contexts/AuthContext';
import HomeShowcase, { HomeLogos, HomeTestimonials, useWebsiteTotals } from '../components/home/HomeShowcase';

const PILLARS = [
  {
    icon: Globe,
    title: 'Your brand, your domain',
    text: 'Registration on your own domain, branded forms and a branded sign-in page for your team. Attendees see your event, not ours.',
  },
  {
    icon: CreditCard,
    title: 'Money goes to you',
    text: 'Collect online through Cashfree or PayU with settlement straight to the organiser, or accept UPI/bank transfers with proof upload.',
  },
  {
    icon: WifiOff,
    title: 'Built for the venue floor',
    text: 'Check-in keeps working when the Wi-Fi doesn’t: offline scanning, a local LAN server and kiosk desks that sync back later.',
  },
];

const PHASES = [
  {
    id: 'before',
    label: 'Before the event',
    features: [
      { icon: ClipboardList, title: 'Form builder', text: 'Any field type, conditional questions, uploads, per-option pricing and early-bird schedules.' },
      { icon: CreditCard, title: 'Online & offline payments', text: 'Cashfree and PayU checkout, split settlements, payment-proof approvals and auto-approve rules.' },
      { icon: Ticket, title: 'Coupons & invite links', text: 'Discount codes, institute invite links with access codes, free or discounted places per group.' },
      { icon: Mail, title: 'Email & WhatsApp', text: 'Confirmation with QR, receipts and invoices, bulk mailers to filtered attendee lists.' },
      { icon: Receipt, title: 'GST invoices & refunds', text: 'Tax invoices, platform-fee invoices, credit notes and one-click gateway refunds.' },
      { icon: Users, title: 'Self-service portal', text: 'Attendees view, edit, download or cancel their registration until your cutoff.' },
    ],
  },
  {
    id: 'during',
    label: 'At the venue',
    features: [
      { icon: QrCode, title: 'QR, Face ID & RFID', text: 'Scan with phones, hardware readers or cameras. Face recognition and RFID cards when you need speed.' },
      { icon: ScanLine, title: 'Multi-purpose scanning', text: 'Entry, meals, kits, sessions: each purpose with its own rules, counts and blocked categories.' },
      { icon: Printer, title: 'Badge printing', text: 'Design badges once, print on arrival from the desk, share to AirPrint on iPad or print in bulk.' },
      { icon: MonitorSmartphone, title: 'Desk & on-spot', text: 'Kiosk desk, on-spot and group registration with its own shorter form and capacity.' },
      { icon: WifiOff, title: 'Offline & LAN mode', text: 'Cached attendee lists, background sync and a local server for venues without internet.' },
      { icon: Lock, title: 'Lockers & cloakroom', text: 'Assign shared or single lockers and track who has what, right from the registration.' },
    ],
  },
  {
    id: 'after',
    label: 'After the event',
    features: [
      { icon: Award, title: 'Certificates', text: 'Generate attendance certificates in bulk or print them at the exit desk.' },
      { icon: BarChart3, title: 'Live analytics', text: 'Registrations, revenue, check-ins by purpose and category, with shareable live stats links.' },
      { icon: FileSpreadsheet, title: 'Exports', text: 'CSV and Excel with your chosen columns, or a ZIP with every uploaded document.' },
      { icon: ShieldCheck, title: 'Audit trail', text: 'Every approval, edit, refund and scan is recorded with who did it and when.' },
      { icon: KeyRound, title: 'Team roles', text: 'Admins, editors and viewers, with per-event control over which buttons each role sees.' },
      { icon: BadgeCheck, title: 'Testimonials & gallery', text: 'Collect feedback and showcase photos and past events on your public page.' },
    ],
  },
];

const SOLUTIONS = [
  { icon: Stethoscope, title: 'Medical & CME conferences', text: 'Delegate categories, workshop add-ons, institute invites, kits and certificates.' },
  { icon: GraduationCap, title: 'Academic & college events', text: 'Student pricing by email domain, fests, symposia and convocations.' },
  { icon: Building2, title: 'Corporate & MICE', text: 'Invite-only events, approvals, branded domains and on-site badge printing.' },
  { icon: Landmark, title: 'Government & associations', text: 'GST invoices, audit trails, offline venues and role-based access.' },
  { icon: Sparkles, title: 'Exhibitions & expos', text: 'High-volume walk-ins, kiosks, multi-hall scanning and live footfall stats.' },
  { icon: CalendarCheck, title: 'Workshops & meetups', text: 'Quick free or paid forms, waitlists, reminders and attendance.' },
];

const STEPS = [
  { title: 'Set up your event', text: 'Pick a form template or build your own, set pricing and connect your payment account.' },
  { title: 'Share the link', text: 'Publish on your site or your own domain, send invite links to institutes and track sign-ups live.' },
  { title: 'Check people in', text: 'Scan QR or faces at the gate, print badges at the desk, and keep going even offline.' },
  { title: 'Wrap up', text: 'Issue certificates, refunds and invoices, then export everything for your records.' },
];

const FAQS = [
  ['Who receives the registration payments?', 'You do. Online payments settle to the organiser’s account through Cashfree or PayU; offline payments go directly to your UPI or bank account and you approve them with the uploaded proof.'],
  ['Can we use our own domain?', 'Yes. Each event can run on a domain like register.yourevent.in, including a branded sign-in page for your team.'],
  ['What if the venue internet fails?', 'Scanning works offline from a cached attendee list and syncs when you’re back online. For large venues we can run a local LAN server.'],
  ['Do you issue GST invoices?', 'Yes. Turn on GST invoicing per event; attendees receive the tax invoice with their receipt, and refunds generate credit notes.'],
  ['Can volunteers use it without seeing everything?', 'Yes. Invite editors and viewers per event and choose exactly which buttons and data each role can see.'],
];

const INTEGRATIONS = ['Cashfree', 'PayU', 'UPI', 'WhatsApp', 'Email (SES)', 'Excel & CSV', 'Thermal printers', 'AWS India'];

const TRUST = [
  { icon: ShieldCheck, title: 'Role-based access', text: 'Admins, editors and viewers per event; volunteers see only what you allow.' },
  { icon: Lock, title: 'Secure payments', text: 'Card and UPI data never touch our servers; gateways handle PCI compliance.' },
  { icon: Landmark, title: 'Hosted in India', text: 'Data stored on AWS Mumbai. Exports and deletion on request.' },
  { icon: Receipt, title: 'Audit & compliance', text: 'Every approval, refund and scan is logged; GST invoices and credit notes built in.' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [phase, setPhase] = useState('before');

  const totals = useWebsiteTotals();
  const { hash } = useLocation();

  // Links like /#check-in from other pages land here before the section has rendered.
  useEffect(() => {
    if (!hash || loading || isAuthenticated) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, loading, isAuthenticated]);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  const active = PHASES.find((p) => p.id === phase) || PHASES[0];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#160a2b] text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.28),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(139,92,246,0.35),transparent_55%)]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2 lg:items-center lg:px-8 lg:py-28">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-violet-200">
              <Sparkles className="h-3.5 w-3.5" /> Registration · Payments · Check-in · Badges
            </p>
            <h1 className="mt-5 text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Event registration and on-site check-in, <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-orange-300 bg-clip-text text-transparent">end to end.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-slate-300">
              One platform for conferences and large events: branded registration forms, payments that settle to you, institute invites, QR and face check-in, badge printing and live analytics.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className={PRIMARY_BTN}>
                <Link to="/demo">Book a demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10">
                <Link to="/events">See live events</Link>
              </Button>
            </div>
            <ul className="mt-8 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
              {['Your own domain & branding', 'Cashfree & PayU payouts', 'Works offline at the venue', 'GST invoices & refunds'].map((item) => (
                <li key={item} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />{item}</li>
              ))}
            </ul>
          </div>

          {/* Product sketch */}
          <div className="relative mx-auto w-full max-w-md lg:max-w-none" aria-hidden="true">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-2xl backdrop-blur sm:p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Main hall · Entry</span>
                <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-300">Live</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                {[['Registered', '2,481'], ['Checked in', '1,936'], ['Kits', '1,702']].map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-white/5 p-3">
                    <div className="text-xl font-bold sm:text-2xl">{value}</div>
                    <div className="text-[11px] text-slate-400">{label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2">
                {[['Dr. A. Sharma', 'Delegate · Paid', true], ['Priya N.', 'PG Student · Invite', true], ['R. Verma', 'Faculty · Pending', false]].map(([name, meta, ok]) => (
                  <div key={name} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/40 text-xs font-semibold">{name.split(' ').map((p) => p[0]).slice(0, 2).join('')}</span>
                      <div>
                        <div className="text-sm font-medium">{name}</div>
                        <div className="text-[11px] text-slate-400">{meta}</div>
                      </div>
                    </div>
                    {ok
                      ? <span className="flex items-center gap-1 text-xs text-emerald-300"><CheckCircle2 className="h-4 w-4" />Checked in</span>
                      : <span className="text-xs text-amber-300">Awaiting payment</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-5 -left-3 hidden items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm shadow-xl sm:flex">
              <Printer className="h-4 w-4 text-orange-300" /> Badge printed
            </div>
            <div className="absolute -right-3 -top-4 hidden items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm shadow-xl sm:flex">
              <ScanFace className="h-4 w-4 text-emerald-300" /> Face matched
            </div>
          </div>
        </div>
      </section>

      <HomeLogos />

      <section className="border-b bg-white py-8" aria-label="Works with">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-2 px-4 sm:px-6 lg:px-8">
          <span className="mr-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Works with</span>
          {INTEGRATIONS.map((name) => (
            <span key={name} className="rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-600">{name}</span>
          ))}
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl border border-gray-200 p-6 transition hover:border-violet-200 hover:shadow-md">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-600"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-gray-600">{text}</p>
              </div>
            ))}
          </div>
          {(totals.events > 0 || totals.registrations > 0) && (
            <dl className="mt-12 grid grid-cols-2 gap-6 rounded-2xl bg-slate-50 p-6 text-center sm:grid-cols-4">
              {[
                [totals.events.toLocaleString('en-IN'), 'events showcased'],
                [totals.registrations.toLocaleString('en-IN'), 'registrations handled'],
                ['4', 'ways to check in'],
                ['2', 'payment gateways'],
              ].map(([value, label]) => (
                <div key={label} className="flex flex-col">
                  <dt className="text-sm text-gray-500">{label}</dt>
                  <dd className="text-3xl font-bold text-gray-900 order-first">{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </section>

      {/* Features by phase */}
      <section id="features" className="scroll-mt-20 bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-violet-600">Features</p>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl">Everything from the first form to the last certificate</h2>
          </div>
          <div role="tablist" aria-label="Event phase" className="mx-auto mt-8 flex max-w-xl gap-1 overflow-x-auto rounded-xl bg-white p-1 shadow-sm">
            {PHASES.map((p) => (
              <button key={p.id} type="button" role="tab" aria-selected={phase === p.id}
                onClick={() => setPhase(p.id)}
                className={`flex-1 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition ${phase === p.id ? 'bg-violet-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div role="tabpanel" className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {active.features.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                <Icon className="h-6 w-6 text-violet-600" />
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Check-in spotlight */}
      <section id="check-in" className="scroll-mt-20 bg-[#160a2b] py-16 text-white sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-orange-300">On-site</p>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl">Fast lines at the gate, badges on arrival</h2>
            <p className="mt-4 text-slate-300">
              Staff scan from any phone or a USB reader. Each scan purpose (entry, lunch, kit, hall) keeps its own count, and the desk prints the badge the moment someone is found.
            </p>
            <ul className="mt-6 space-y-3 text-slate-200">
              {['Duplicate and blocked-category scans flagged instantly', 'Kiosk desk with on-spot and group registration', 'Printing on thermal or A4 printers, or AirPrint from iPad', 'Scan statistics shareable with your committee'].map((item) => (
                <li key={item} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />{item}</li>
              ))}
            </ul>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {CHECKIN_METHODS.map(({ slug, icon: Icon, title, card }) => (
              <Link key={slug} to={`/check-in/${slug}`}
                className="group rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:border-orange-300/50 hover:bg-white/10">
                <Icon className="h-6 w-6 text-orange-300" />
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="mt-1 text-sm text-slate-400">{card}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-300 group-hover:text-orange-200">
                  How it works <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="scroll-mt-20 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-violet-600">Solutions</p>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl">Made for the events you run</h2>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SOLUTIONS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="group rounded-2xl border border-gray-200 p-6 transition hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-lg">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-orange-500 text-white"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-semibold">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HomeShowcase showLogos={false} />

      {/* How it works */}
      <section id="how-it-works" className="scroll-mt-20 bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold sm:text-4xl">Live in a day, not a month</h2>
          <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, index) => (
              <li key={step.title} className="relative rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-sm font-bold text-white">{index + 1}</span>
                <h3 className="mt-4 font-semibold">{step.title}</h3>
                <p className="mt-1.5 text-sm text-gray-600">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <HomeTestimonials />

      {/* Trust */}
      <section className="bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-violet-600">Trust</p>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl">Built for organisers who answer to a committee</h2>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                <Icon className="h-6 w-6 text-violet-600" />
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-20 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold sm:text-4xl">Questions organisers ask</h2>
          <div className="mt-8 divide-y rounded-2xl border">
            {FAQS.map(([q, a]) => (
              <details key={q} className="group p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
                  {q}
                  <ChevronDown className="h-5 w-5 shrink-0 text-gray-400 transition group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-gray-600">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-gradient-to-r from-violet-700 via-fuchsia-700 to-orange-600 py-16">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">Planning your next event?</h2>
          <p className="max-w-2xl text-lg text-violet-100">Tell us about it and we’ll set up a walkthrough with your own form, pricing and badge.</p>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <Button asChild size="lg" variant="secondary">
              <Link to="/demo">Book a demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10">
              <Link to="/contact-us">Contact us</Link>
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default HomePage;
